import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Bed, Bath, MapPin, Building2 } from 'lucide-react';
import { useStories } from '@/hooks/useStories';
import { StoryViewerDialog } from './StoryViewerDialog';
import { useTranslation } from 'react-i18next';
import { pickLocalized } from '@/hooks/useLandingContent';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPrice } from '@/lib/formatPrice';
import { optimizedImageUrl } from '@/lib/imageOptimizer';
import { getRecentlyViewed } from '@/lib/recentlyViewed';
import { trackLandingEvent } from '@/lib/landingAnalytics';

export function StoriesRow() {
  const { data: stories, isLoading } = useStories();
  const { i18n } = useTranslation();
  const { displayCurrency, rate } = useCurrency();
  const lang = i18n.language || 'en';
  const ref = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const [startIdx, setStartIdx] = useState(0);
  const viewedSet = useMemo(() => new Set(getRecentlyViewed()), [open]);

  useEffect(() => {
    if (!stories || stories.length === 0 || !sectionRef.current) return;
    const el = sectionRef.current;
    const obs = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && e.intersectionRatio >= 0.5) {
          const featuredIds = stories.slice(0, 8).map((s: any) => s.id);
          const first = stories[0];
          trackLandingEvent({
            event_type: 'stories_impression',
            entity_type: first ? 'property' : null,
            entity_id: first?.id ?? null,
            metadata: { count: stories.length, featured_ids: featuredIds },
            dedupKey: 'stories',
          });
          obs.disconnect();
          break;
        }
      }
    }, { threshold: [0.5] });
    obs.observe(el);
    return () => obs.disconnect();
  }, [stories]);

  if (isLoading || !stories || stories.length === 0) return null;

  const slidePage = (dir: 'left' | 'right') => {
    if (!ref.current) return;
    const w = ref.current.clientWidth;
    ref.current.scrollBy({ left: dir === 'left' ? -w : w, behavior: 'smooth' });
  };

  const openStory = (i: number) => {
    setStartIdx(i);
    setOpen(true);
    const s = stories[i];
    if (s) trackLandingEvent({ event_type: 'story_open', entity_type: 'property', entity_id: s.id, metadata: { position: i } });
  };

  // (formatArea no longer used; replaced by structured highlights row)

  return (
    <section ref={sectionRef} className="container py-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl md:text-2xl font-bold">Stories</h2>
      </div>
      <div className="relative">
        <button
          onClick={() => slidePage('left')}
          aria-label="Previous stories"
          className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full border bg-card shadow flex items-center justify-center hover:bg-muted"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          onClick={() => slidePage('right')}
          aria-label="Next stories"
          className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full border bg-card shadow flex items-center justify-center hover:bg-muted"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        <div ref={ref} className="flex gap-3 overflow-x-auto scrollbar-hide snap-x snap-mandatory scroll-smooth pb-2">
          {stories.map((s, i) => {
            const title = pickLocalized(s.title, lang);
            const img = optimizedImageUrl(s.cover_url || '', { width: 320, height: 240, quality: 75 });
            const viewed = viewedSet.has(s.id);
            return (
              <button
                key={s.id}
                onClick={() => openStory(i)}
                className="snap-start shrink-0 basis-[calc(33.333%-8px)] sm:basis-[30%] md:basis-[18%] lg:basis-[16%] text-left group"
              >
                <div className="relative aspect-[4/3] rounded-lg overflow-hidden bg-muted border">
                  {s.cover_url && (
                    <img src={img} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  )}
                  {/* Agency logo top-left */}
                  <div className="absolute top-2 left-2 h-9 w-9 rounded-full bg-background border border-border shadow flex items-center justify-center overflow-hidden">
                    {s.agency_logo_url ? (
                      <img src={s.agency_logo_url} alt={s.agency_name || 'Agency'} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                  {viewed && (
                    <span className="absolute top-2 right-2 px-1.5 py-0.5 bg-background/90 text-foreground text-[9px] font-bold rounded uppercase tracking-wide">
                      Viewed
                    </span>
                  )}
                </div>
                <div className="mt-2 px-1 space-y-0.5">
                  <div className="font-bold text-sm text-foreground truncate">
                    {formatPrice(s.price ?? 0, s.currency || 'ETB', displayCurrency, rate)}
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    {s.bedrooms != null && (
                      <span className="flex items-center gap-0.5"><Bed className="h-3 w-3" />{s.bedrooms}</span>
                    )}
                    {s.bathrooms != null && (
                      <span className="flex items-center gap-0.5"><Bath className="h-3 w-3" />{s.bathrooms}</span>
                    )}
                    {s.area_sqm != null && (
                      <span className="truncate">{Math.round(s.area_sqm)} {s.area_unit || 'm²'}</span>
                    )}
                  </div>
                  {(s.sub_city || s.city) && (
                    <div className="flex items-center gap-1 text-[11px] text-muted-foreground truncate">
                      <MapPin className="h-3 w-3 shrink-0" />
                      <span className="truncate">{[s.sub_city, s.city].filter(Boolean).join(', ')}</span>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <StoryViewerDialog stories={stories} startIndex={startIdx} open={open} onOpenChange={setOpen} />
    </section>
  );
}
